"use client";

import React from "react";

import BannerCta from "@/components/banners/BannerCta";
import OptimizedImage from "@/components/common/images/OptimizedImage";
import { Link } from "@/i18n/routing";
import { PublicBannerItem } from "@/lib/banners/types";
import { cn } from "@/lib/utils";

type PublicBannerCreativeProps = {
  banner: PublicBannerItem;
  alt?: string;
  imageClassName?: string;
  /** Defaults to `banner.width`. Pass explicitly to override (e.g. fixed mobile width). */
  width?: number;
  /** Defaults to `banner.height`. Pass `0` to opt into OptimizedImage fill mode. */
  height?: number;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  loading?: "eager" | "lazy";
  isBanner?: boolean;
  /** When provided, the image is wrapped in an i18n Link (independent of the CTA href). */
  imageHref?: string;
  showCta?: boolean;
  /**
   * Banner used to source the CTA, when it should differ from the displayed image banner
   * (e.g. a slot's sole CTA persisting across rotating images). Defaults to `banner`.
   * Pass `null` to render no CTA.
   */
  ctaBanner?: PublicBannerItem | null;
  ctaClassName?: string;
  ctaLabelFallback?: string;
  ctaHrefFallback?: string;
  /** When set, the CTA button calls this instead of following its link. */
  onCtaClick?: () => void;
  /** Applies a slow Ken Burns zoom/pan to the banner image. */
  withKenBurns?: boolean;
};

/**
 * Renders a single public banner creative: an optimized image plus its optional CTA.
 *
 * It intentionally returns a fragment (no positioning wrapper) so it can drop into the
 * existing placements without changing their layout. Use the props to mirror each
 * placement's current image/CTA styling exactly.
 */
const PublicBannerCreative = ({
  banner,
  alt,
  imageClassName,
  width,
  height,
  sizes,
  quality,
  priority = false,
  loading,
  isBanner = true,
  imageHref,
  showCta = true,
  ctaBanner,
  ctaClassName,
  ctaLabelFallback,
  ctaHrefFallback,
  onCtaClick,
  withKenBurns = false,
}: PublicBannerCreativeProps) => {
  const ctaSource = ctaBanner === undefined ? banner : ctaBanner;
  const image = (
    <OptimizedImage
      src={banner.assetUrl}
      width={width ?? banner.width}
      height={height ?? banner.height}
      alt={alt ?? banner.bannerName ?? "banner"}
      className={cn(imageClassName, withKenBurns && "animate-kenburns")}
      sizes={sizes}
      quality={quality}
      isBanner={isBanner}
      {...(priority ? { priority: true } : { loading: loading ?? "lazy" })}
    />
  );

  return (
    <>
      {imageHref ? <Link href={imageHref as any}>{image}</Link> : image}
      {showCta && ctaSource ? (
        <BannerCta
          banner={ctaSource}
          labelFallback={ctaLabelFallback}
          hrefFallback={ctaHrefFallback}
          className={ctaClassName}
          onClick={onCtaClick}
        />
      ) : null}
    </>
  );
};

export default PublicBannerCreative;
